import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { ApiClient } from '@app/api/api-client';
import { ApiTypes } from '@app/api/api-types';
import { Api } from '@app/api/generated/api';
import { ContextService } from '@core/providers/context/context.service';
import { CONSTANTS } from '@shared/constants';
import { SharedComponentViewType } from '@shared/interfaces/common.interface';
import { IDropdownItem } from '@shared/interfaces/dropdown/dropdown-item.interface';
import {
  I18nContextLanguage,
  I18nCountriesAndTimeZonesDropdown,
  I18nDefaultLanguage,
  I18nSupportedLanguage,
  I18nTimeZonesByCountry,
} from '@shared/interfaces/i18n.interface';

@Service()
export class I18nBackendService {
  private api = inject(ApiClient);
  private translateService = inject(TranslateService);
  private contextService = inject(ContextService);

  private supportedLanguagesUuid: string | null = null;
  private supportedLanguagesRequest$: Observable<I18nContextLanguage[]> | null = null;
  private defaultLanguageUuid: string | null = null;
  private defaultLanguageRequest$: Observable<I18nDefaultLanguage> | null = null;

  constructor() {
    this.contextService.onSessionContextUuidChange$.subscribe(() => this.resetCache());
  }

  getAllSupportedCountries$(): Observable<ApiTypes.CountryResponseDto[]> {
    return this.api.get(Api.i18n.countries, {});
  }

  getCountriesWithTimeZones$(): Observable<I18nCountriesAndTimeZonesDropdown> {
    return this.getAllSupportedCountries$().pipe(
      map(supportedCountries => {
        const countries: IDropdownItem[] = [];
        const timeZonesByCountry: I18nTimeZonesByCountry[] = [];

        supportedCountries.forEach(supportedCountry => {
          countries.push({
            id: supportedCountry.code,
            text: supportedCountry.name,
          });
          const availableTimeZones: IDropdownItem[] = supportedCountry.timezones.map(zone => ({
            id: zone.timezone,
            text: `${zone.gmtDifference} ${zone.timezone}`,
            value: zone.timezone,
          }));

          timeZonesByCountry.push({
            availableTimeZones,
            code: supportedCountry.code,
          });
        });

        return {
          countries,
          timeZonesByCountry,
        };
      })
    );
  }

  getAllSupportedLanguages$(): Observable<ApiTypes.LanguageResponseDto[]> {
    return this.api.get(Api.i18n.languages, {});
  }

  // TODO remove it
  getSupportedLanguagesForAccount$(
    uuid: string
  ): Observable<Api.i18n.accounts.languages.GetResult> {
    return this.api
      .get(Api.i18n.accounts.languages, { pathParams: { accountUuid: uuid } })
      .pipe(map(supportedLanguages => supportedLanguages.filter(lang => lang.isActive)));
  }

  getSupportedLanguages$(uuid: string, contextType: string): Observable<I18nContextLanguage[]> {
    return contextType === CONSTANTS.CONTEXT.ACCOUNT
      ? this.api.get(Api.i18n.accounts.languages, { pathParams: { accountUuid: uuid } })
      : this.api.get(Api.i18n.venues.languages, { pathParams: { venueUuid: uuid } });
  }

  getLanguageValidationStatus$(
    uuid: string,
    contextType: string
  ): Observable<ApiTypes.TranslationCoverageResponseDto[]> {
    return contextType === CONSTANTS.CONTEXT.ACCOUNT
      ? this.api.get(Api.i18n.accounts.translationCoverage, {
          pathParams: { accountUuid: uuid },
        })
      : this.api.get(Api.i18n.venues.translationCoverage, {
          pathParams: { venueUuid: uuid },
        });
  }

  activateOrDeactivateLanguage$(
    uuid: string,
    contextType: string,
    code: string,
    isActive: boolean
  ): Observable<unknown> {
    this.resetCache();

    return contextType === CONSTANTS.CONTEXT.ACCOUNT
      ? this.api.patch(Api.i18n.accounts.languages.activation, {
          pathParams: { accountUuid: uuid, code },
          body: { isActive },
        })
      : this.api.patch(Api.i18n.venues.languages.activation, {
          pathParams: { venueUuid: uuid, code },
          body: { isActive },
        });
  }

  enforceOrUnforceLanguage$(uuid: string, code: string, isEnforced: boolean): Observable<unknown> {
    this.resetCache();

    return this.api.patch(Api.i18n.accounts.languages.enforcement, {
      pathParams: { accountUuid: uuid, code },
      body: { isEnforced },
    });
  }

  setDefaultLanguage$(uuid: string, contextType: string, code: string): Observable<unknown> {
    this.resetCache();

    return contextType === CONSTANTS.CONTEXT.ACCOUNT
      ? this.api.patch(Api.i18n.accounts.languages.default.byCode, {
          pathParams: { accountUuid: uuid, code },
        })
      : this.api.patch(Api.i18n.venues.languages.default.byCode, {
          pathParams: { venueUuid: uuid, code },
        });
  }

  getSupportedLanguagesForContext$(
    uuid: string,
    viewType: SharedComponentViewType
  ): Observable<I18nContextLanguage[]> {
    if (this.supportedLanguagesRequest$ && this.supportedLanguagesUuid === uuid) {
      return this.supportedLanguagesRequest$;
    }

    this.supportedLanguagesUuid = uuid;
    this.supportedLanguagesRequest$ = this.getSupportedLanguages$(
      uuid,
      viewType === SharedComponentViewType.ACCOUNT
        ? CONSTANTS.CONTEXT.ACCOUNT
        : CONSTANTS.CONTEXT.VENUE
    ).pipe(
      map(supportedLanguages => supportedLanguages.filter(lang => lang.isActive)),
      share()
    );

    return this.supportedLanguagesRequest$;
  }

  getDefaultLanguageForContext$(
    uuid: string,
    viewType: SharedComponentViewType
  ): Observable<I18nDefaultLanguage> {
    if (this.defaultLanguageRequest$ && this.defaultLanguageUuid === uuid) {
      return this.defaultLanguageRequest$;
    }

    this.defaultLanguageUuid = uuid;
    this.defaultLanguageRequest$ = (
      viewType === SharedComponentViewType.ACCOUNT
        ? this.api.get(Api.i18n.accounts.languages.default, {
            pathParams: { accountUuid: uuid },
          })
        : this.api.get(Api.i18n.venues.languages.default, {
            pathParams: { venueUuid: uuid },
          })
    ).pipe(share());

    return this.defaultLanguageRequest$;
  }

  resetCache(): void {
    this.supportedLanguagesUuid = null;
    this.supportedLanguagesRequest$ = null;
    this.defaultLanguageUuid = null;
    this.defaultLanguageRequest$ = null;
  }

  getSupportedLanguagesForVenue$(uuid: string): Observable<Api.i18n.venues.languages.GetResult> {
    return this.api
      .get(Api.i18n.venues.languages, { pathParams: { venueUuid: uuid } })
      .pipe(map(supportedLanguages => supportedLanguages.filter(lang => lang.isActive)));
  }

  getAllLanguagesForVenue$(uuid: string): Observable<Api.i18n.venues.languages.GetResult> {
    return this.api.get(Api.i18n.venues.languages, { pathParams: { venueUuid: uuid } });
  }

  getTranslatedLanguageName(langCode: string) {
    return this.translateService.instant(`SHARED.LANGUAGES.${langCode.toUpperCase()}`);
  }

  sortLanguagesByNameAsc(languages: I18nSupportedLanguage[]): I18nSupportedLanguage[] {
    return languages
      .slice()
      .sort((a: I18nSupportedLanguage, b: I18nSupportedLanguage) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
  }
}
